import React, {
  useRef,
  useCallback,
  useEffect,
  KeyboardEvent,
  ClipboardEvent,
} from 'react';
import styled from 'styled-components';

const StyledWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
`;

const StyledCell = styled.input<{
  $hasValue: boolean;
  $hasError: boolean;
  $disabled: boolean;
}>`
  width: 3rem;
  height: 3.25rem;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 400;
  font-family: inherit;
  color: #232323;
  border: 0.0625rem solid
    ${({ $hasError }) => ($hasError ? '#F04438' : '#d7d3d0')};
  border-radius: 0.625rem;
  background: ${({ $disabled }) => ($disabled ? '#f5f5f4' : '#ffffff')};
  outline: none;
  transition: border-color 0.15s ease;
  caret-color: transparent;

  &::placeholder {
    color: #57534e;
    font-size: 0.875rem;
  }

  &:focus {
    border-color: ${({ $hasError }) =>
      $hasError ? '#F04438' : 'var(--rg-primary-color, #165e5f)'};
  }
`;

const OTP_LENGTH = 6;

const CELL_KEYS = ['d1', 'd2', 'd3', 'd4', 'd5', 'd6'];

type OtpInputProps = {
  values: string[];
  hasError?: boolean;
  disabled?: boolean;
  onChange: (values: string[]) => void;
  onComplete: (values: string[]) => void;
};

export function OtpInput({
  values,
  hasError = false,
  disabled = false,
  onChange,
  onComplete,
}: Readonly<OtpInputProps>) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const focusIndex = useCallback((i: number) => {
    inputRefs.current[i]?.focus();
  }, []);

  useEffect(() => {
    if (values.every((v) => v === '')) {
      focusIndex(0);
    }
  }, [values, focusIndex]);

  const handleChange = useCallback(
    (index: number, value: string) => {
      const digits = value.replace(/\D/g, '');

      if (digits.length > 1) {
        const chars = digits.slice(0, OTP_LENGTH).split('');
        const next = values.map((v, i) => (i < chars.length ? chars[i] : v));
        onChange(next);

        const nextIndex = Math.min(chars.length, OTP_LENGTH - 1);
        focusIndex(nextIndex);

        if (chars.length === OTP_LENGTH) {
          setTimeout(() => onComplete(next), 0);
        }
        return;
      }

      const digit = digits.slice(-1);
      const next = values.map((v, i) => (i === index ? digit : v));
      onChange(next);

      if (digit && index < OTP_LENGTH - 1) {
        focusIndex(index + 1);
      }

      if (digit && index === OTP_LENGTH - 1) {
        if (next.every((v) => /^\d$/.test(v))) {
          setTimeout(() => onComplete(next), 0);
        }
      }
    },
    [values, onChange, onComplete, focusIndex]
  );

  const handleKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        if (values[index]) {
          onChange(values.map((v, i) => (i === index ? '' : v)));
        } else if (index > 0) {
          onChange(values.map((v, i) => (i === index - 1 ? '' : v)));
          focusIndex(index - 1);
        }
        e.preventDefault();
      } else if (e.key === 'ArrowLeft' && index > 0) {
        focusIndex(index - 1);
      } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
        focusIndex(index + 1);
      }
    },
    [values, onChange, focusIndex]
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, OTP_LENGTH);
      if (!pasted) return;

      const chars = pasted.split('');
      const next = values.map((v, i) => (i < chars.length ? chars[i] : v));
      onChange(next);

      const nextIndex = Math.min(chars.length, OTP_LENGTH - 1);
      focusIndex(nextIndex);

      if (chars.length === OTP_LENGTH) {
        setTimeout(() => onComplete(next), 0);
      }
    },
    [values, onChange, onComplete, focusIndex]
  );

  return (
    <StyledWrapper>
      {values.map((digit, index) => (
        <StyledCell
          key={CELL_KEYS[index]}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          placeholder="–"
          value={digit}
          $hasValue={!!digit}
          $hasError={hasError}
          $disabled={disabled}
          disabled={disabled}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={index === 0 ? handlePaste : undefined}
          aria-label={`Digit ${index + 1}`}
        />
      ))}
    </StyledWrapper>
  );
}
